import * as React from "react";
import { RouteComponentProps, withRouter } from "react-router";
import { Button } from "semantic-ui-react";

import { PageActionProps } from "../../../components";
import { AssistantEditForm } from "../../Assistant/EditForm";
import { AssistantServices } from "../../../services";
import { Assistant, WebComponents } from "../../../interfaces";
import { URLS, useAccountSettings } from "../../../utils";

import { SocialGrid } from "../../../styling/baseStyle";
import "./styles.scss";
import { UserStatus } from "@socialcoach/services";
import { useMemo } from "react";
import { PageWrapper } from "../../../components/PageWrapper";
type Props = RouteComponentProps;

const EditAssistantFC: React.FC<Props> = ({ history, match: { params } }) => {
  const assistantId = params["userId"];
  const [assistant, setAssistant] = React.useState<Assistant>();

  const formSubmitRef = React.useRef<HTMLFormElement>(null);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [assistantEdited, setAssistantEdited] = React.useState<boolean>(false);

  const { hasWritePermission } = useAccountSettings();
  const withWritePermission = hasWritePermission(WebComponents.ASSISTANTS);

  React.useEffect(() => {
    loadAssistant().catch(console.error);
  }, [assistantId]);

  const loadAssistant = async () => {
    if (assistantId) {
      setLoading(true);
      const data = await AssistantServices.get(assistantId);
      setAssistant(data);
      setLoading(false);
      setAssistantEdited(false);
    }
  };

  const goToAssistantsList = () => {
    setLoading(false);
    history.goBack();
  };

  const updateAssistant = () => {
    setLoading(true);
    // Execute handleSubmit action on create sub coach form
    if (formSubmitRef.current) {
      formSubmitRef!.current!.handleSubmit();
    }
  };

  const cancelAssistant = () => {
    setLoading(true);

    AssistantServices.updateAssistant(assistantId, {
      account: { ...assistant?.account, userStatus: UserStatus.SUSPENDED },
    })
      .then(() => {
        alert("Assistant cancelled successfully");
        history.goBack();
      })
      .catch(() => {
        alert("Unknown error. Try again");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const reactivateAssistant = () => {
    setLoading(true);
    AssistantServices.updateAssistant(assistantId, { account: { userStatus: UserStatus.ACTIVE } })
      .then(() => {
        alert("Assistant activated successfully");
      })
      .catch(() => {
        alert("Unknown error. Try again");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const isInactive = useMemo(() => {
    return assistant?.account?.user?.status == UserStatus.SUSPENDED;
  }, [assistant?.account?.user?.status]);

  const customLinkSection = () => {
    return (
      <SocialGrid.Column width={10} floated="right">
        <SocialGrid.Column
          width={assistant?.account?.user?.status == UserStatus.SUSPENDED ? 6 : 8}
          floated="right"
          className={"buttonLeft"}
        >
          <Button
            data-elm-id={`editAssistantBackBtn`}
            fluid
            className={"secondary rounded buttonRight"}
            onClick={goToAssistantsList}
          >
            Back
          </Button>
        </SocialGrid.Column>
        {withWritePermission && (
          <SocialGrid.Column width={isInactive ? 10 : 8} floated="right" className={"buttonRight"}>
            {!isInactive && (
              <>
                <Button
                  data-elm-id={`editAssistantInactiveBtn`}
                  loading={loading}
                  fluid
                  className={"secondary rounded buttonRight"}
                  disabled={loading}
                  onClick={cancelAssistant}
                  basic
                >
                  Inactivate
                </Button>
                <Button
                  data-elm-id={`editAssistantSaveBtn`}
                  fluid
                  className={"primary rounded buttonRight"}
                  onClick={updateAssistant}
                  disabled={!assistantEdited}
                >
                  Save
                </Button>
              </>
            )}
            {isInactive && (
              <Button
                data-elm-id={`editAssistantReactivateBtn`}
                disabled={loading}
                loading={loading}
                fluid
                className={"primary rounded buttonRight"}
                onClick={reactivateAssistant}
              >
                Reactivate
              </Button>
            )}
          </SocialGrid.Column>
        )}
      </SocialGrid.Column>
    );
  };

  const pageActions = useMemo(() => {
    const actions: PageActionProps[] = [];
    if (withWritePermission) {
      if (isInactive) {
        actions.push({ text: "Reactivate", onClick: reactivateAssistant, loading: loading });
      } else {
        actions.push({ text: "Save", onClick: updateAssistant, loading: loading });
        actions.push({ text: "Cancel Assistant Account", onClick: cancelAssistant, loading: loading });
      }
    }

    return actions;
  }, [withWritePermission, isInactive, loading]);

  return (
    <PageWrapper
      title="Edit Assistant"
      breadcrumbs={[{ name: "Users", link: URLS.coach.assistants }]}
      actions={pageActions}
    >
      <div className={!withWritePermission || isInactive ? "disabledForm" : ""}>
        {assistant && (
          <AssistantEditForm
            assistant={assistant}
            formSubmitRef={formSubmitRef}
            errorHandler={() => setLoading(false)}
            successHandler={loadAssistant}
            withWritePermission={withWritePermission}
            onChangeHandler={() => setAssistantEdited(true)}
          />
        )}
      </div>
    </PageWrapper>
  );
};

export const EditAssistant = withRouter(EditAssistantFC);
